// ===== WorkInlineCards: 업무 후보 확인 카드 (records-ssot 4단계, 결정 F5·F20·F21) =====
// 채팅 인라인 카드·업무 후보함·방 배지 팝오버가 **같은 카드 하나**를 쓴다(buildCard). 카드는 outbox 레코드의 UI
// projection 이며 채팅 메시지·에이전트 발화가 아니다(CAP15 무발화 경계). 세션 로그(JSONL)에 저장하지 않고 매 렌더
// 시점에 outbox 를 원천으로 합성한다(D01).
//
// 카드 구성(F20): 감지한 내용 한 줄 · 제목(그 자리 수정) · 담당(그 자리 수정, 팀원 피커) · 대상(새 업무 / 감지가 확정한
// 기존 업무 붙이기) · 현재 값 한 줄(우선순위 · 마감 · 프로젝트 · 협업 N명, 누르면 네 칸이 펼쳐진다 — F20 확장, 새 업무·
// 하위 업무에서만) · [등록] · [무시]. 감지가 "끝난 것 같아요"(completionHint)를 남겼으면 근거와 함께 보이고 사람이 [무시]를
// 누른다(자동 무시 없음, F19). [등록]은 POST /api/work/outbox/:id/register 한 번이며 검토 문서·필수 입력·사전
// 확인 단계가 없다. 처리된 후보는 접힌 한 줄(업무로 등록됨 → 보드 상세 딥링크 / 무시됨 / 만료됨)로 남는다.
//
// 채팅 라이브 경로: work-detected 이벤트(ws-handlers.ts) → 뷰잉 세션 게이트 → GET /api/work/outbox/:id → 삽입.
// 채팅 복원 경로(D02, CAP06): 방 열람 시 GET /api/work/outbox 전체에서 contextWindow.chatId 필터 → createdAt 순 삽입.
// 두 경로 모두 카드를 감지 창의 마지막 메시지 뒤에 놓는다(_syncPlacement) — 대화 끝에 쌓이지 않는다.
// 채팅 카드는 새 후보만 그린다(사용자 결정 2026-10-06, _isChatCardRecord): 감지 창 위치(anchorSeq)가 없거나 보드 재정리
// 기준 시각(_CHAT_CARD_CUTOFF) 전에 만든 옛 후보는 채팅에 넣지 않는다 — 옛 후보는 보드 이전 때 모두 정리됐다. 후보함·방
// 배지·업무 첫 화면은 buildCard 를 직접 쓰므로 이 거름과 무관하다.
// data-outbox-id 멱등 — 라이브/복원 경합 시 같은 레코드가 중복 삽입되지 않는다.
// 실패 격리(CAP14): 조회·렌더 실패는 삼켜 채팅 메시지 렌더·전송을 막지 않는다.
//
// records-ssot 라운드 3 레인 E(사용자 결정 2026-10-03):
//   - 종류 전환: 제목 위 [업무 | 프로젝트]. 기본은 업무. 프로젝트면 업무 칸(담당·대상·우선순위·마감·협업자·프로젝트)을
//     감추고 PO(필수, 기본 = 나)·참여자(기본 = 후보의 담당·협업자 + PO)·선택(트랙·고객) 칸을 보인다. [등록]은 같은
//     POST /api/work/outbox/:id/register 에 target {type:"project"} + project{...} 로 간다. 기여도는 여기서 받지 않는다
//     (보드에서 PO 가 입력). 등록 뒤 "프로젝트로 등록됨 → 보드에서 열기" 한 줄로 접힌다.
//   - 프로젝트 모드·생성 카드 코드는 work-inline-cards-project.ts(_createWorkCardProject, 이 파일보다 먼저 로드)에 있다.
//   - 생성 카드(buildCreateCard): 채팅 `/task`·`/project`(command-result next.kind "work-create-card", handleCommandResult)가
//     그 자리에 여는 빈 카드(제목 프리필). 사람이 [등록]해야 생긴다(F19): 업무 → POST /api/work/items(보드 "업무 등록"과
//     같은 수동 등록, 담당은 나), 프로젝트 → POST /api/work/projects. 출처로 지금 채팅 세션을 함께 보낸다.
//   - 더 최신 후보로 대체된 후보(supersededBy): 접힌 줄 "더 최신 후보로 바뀌었어요" + [새 카드 열기].

type WorkCandidateContext = "chat" | "outbox" | "badge";
type WorkCandidateActorRef = { actorId?: string; label?: string };
type WorkCandidateTarget =
  | { type: "new" }
  | { type: "attach"; workItemId: string; kind: "subitem" }
  | { type: "attach"; workItemId: string; kind: "link"; url: string }
  | { type: "attach"; workItemId: string; kind: "file"; fileId: string };
type WorkCandidateRecord = {
  id: string;
  state: string;
  revision?: number;
  bandLabel?: string;
  band?: string;
  chatId?: string;
  excerpt?: string;
  // 감지 창의 마지막 메시지 seq(contextWindow.sourceRefs 의 `<chatId>#msg_<seq>` 중 최대). 채팅 카드는 이 메시지 뒤에 놓인다.
  anchorSeq?: number;
  createdAt?: string;
  proposalId?: string;
  workItemId?: string;
  title: string;
  summary?: string;
  assignee?: WorkCandidateActorRef;
  owner?: WorkCandidateActorRef;
  target?: WorkCandidateTarget;
  expiresAt?: string;
  expiringSoon?: boolean;
  expiredAt?: string;
  dismissedBy?: string;
  convertedBy?: string;
  registeredOwner?: string;
  // 후보 초안(감지·에이전트가 미리 채운 값). 카드의 네 칸 기본값이다.
  priority?: string;
  dueAt?: string;
  project?: { id: string; title: string };
  // 감지가 "같은 채팅에서 나중에 끝난 일"로 본 근거(레인 B, 읽기만). 없으면 아무것도 안 보인다.
  completionHint?: WorkCandidateCompletionHint;
  // 프로젝트로 등록된 후보의 회사 md 프로젝트 id(레인 E).
  projectId?: string;
  // 감지가 같은 일의 새 후보로 대체한 후보면 그 새 후보 id(레인 E).
  supersededBy?: string;
};
type WorkCardKind = "task" | "project";
// 프로젝트 칸(레인 E): PO(필수)·참여자(PO 포함)·선택 트랙·고객.
type WorkProjectFieldState = {
  po: string;
  participants: Array<{ actorId: string; label: string }>;
  tracks: string[];
  clientIds: string[];
  // 카드에서 그 자리에서 추가한 새 고객(등록 때 newClients 로 보낸다, 2026-10-05). key 는 고객 select 의 option 값.
  newClients: Array<{ key: string; name: string; kind: "client" | "partner" }>;
};
type WorkCompanyCatalog = {
  projects: Array<{ id: string; name: string }>;
  clients: Array<{ id: string; name: string; kind?: string }>;
  tracks: Array<{ key: string; label: string }>;
};
type WorkCandidateCompletionHint = { ref?: string; reason?: string; excerpt?: string; detectedAt?: string };
type WorkCandidateCardOptions = {
  context: WorkCandidateContext;
  // 등록·무시·만료 수렴 뒤 호출된다(후보함이 처리됨 구역으로 옮기고, 배지가 다시 센다).
  onResolved?: (record: WorkCandidateRecord) => void;
};
type WorkCandidatePerson = { actorId: string; label: string; isSelf: boolean };
// 카드의 네 칸(우선순위·마감·협업자·프로젝트). due 는 date 입력 값(YYYY-MM-DD, 비면 마감 없음).
type WorkCandidateFieldState = {
  priority: string;
  due: string;
  dueIso: string | null;
  collaborators: Array<{ actorId: string; label: string }>;
  projectId: string | null;
  projectLabel: string | null;
  // 상위 업무(채팅 `/task` 생성 카드만, 2026-10-05). 고르면 그 업무의 하위 업무로 등록한다.
  parentId: string | null;
  parentLabel: string | null;
};
// 상위 업무 후보(보드 API 의 끝나지 않은 최상위 업무). 줄 보조 글에 프로젝트·담당을 보인다.
type WorkParentCandidate = { id: string; title: string; projectId: string | null; projectTitle: string | null; ownerLabel: string | null };
type WorkCandidateFieldName = "priority" | "dueAt" | "collaborators" | "projectId";

function _isWicRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// 서버 JSON은 신뢰하지 않는다 — 부모가 레코드가 아니거나 값 타입이 다르면 "없음"으로 떨어뜨린다.
function _wicStrAt(parent: Record<string, unknown> | null, key: string): string | undefined {
  var value = parent ? parent[key] : undefined;
  return typeof value === "string" ? value : undefined;
}

function _wicObjAt(parent: Record<string, unknown> | null, key: string): Record<string, unknown> | null {
  var value = parent ? parent[key] : undefined;
  return _isWicRecord(value) ? value : null;
}

// ActorStub(actor/actorSource/displaySnapshot) 모양의 값에서 표시용 라벨만 뽑는다.
function _actorLabel(value: unknown): string | undefined {
  if (!_isWicRecord(value)) return undefined;
  var snapshot = _wicObjAt(value, "displaySnapshot");
  return _wicStrAt(snapshot, "label");
}

function _wicActorRef(value: Record<string, unknown> | null): WorkCandidateActorRef | undefined {
  if (!value) return undefined;
  var actorId = _wicStrAt(value, "actorId");
  var label = _wicStrAt(value, "label");
  return actorId || label ? { actorId: actorId, label: label } : undefined;
}

function _wicTarget(value: Record<string, unknown> | null): WorkCandidateTarget | undefined {
  if (!value) return undefined;
  var type = _wicStrAt(value, "type");
  if (type === "new") return { type: "new" };
  if (type !== "attach") return undefined;
  var workItemId = _wicStrAt(value, "workItemId");
  var kind = _wicStrAt(value, "kind");
  if (!workItemId) return undefined;
  if (kind === "subitem") return { type: "attach", workItemId: workItemId, kind: "subitem" };
  var url = _wicStrAt(value, "url");
  if (kind === "link" && url) return { type: "attach", workItemId: workItemId, kind: "link", url: url };
  var fileId = _wicStrAt(value, "fileId");
  if (kind === "file" && fileId) return { type: "attach", workItemId: workItemId, kind: "file", fileId: fileId };
  return undefined;
}

function _wicCompletionHint(value: Record<string, unknown> | null): WorkCandidateCompletionHint | undefined {
  if (!value) return undefined;
  var hint: WorkCandidateCompletionHint = {
    ref: _wicStrAt(value, "ref"),
    reason: _wicStrAt(value, "reason"),
    excerpt: _wicStrAt(value, "excerpt"),
    detectedAt: _wicStrAt(value, "detectedAt"),
  };
  return hint.reason || hint.excerpt ? hint : undefined;
}

function _wicProject(value: Record<string, unknown> | null): { id: string; title: string } | undefined {
  var id = _wicStrAt(value, "id");
  if (!id) return undefined;
  return { id: id, title: _wicStrAt(value, "title") || id };
}

// contextWindow.sourceRefs(`<chatId>#msg_<seq>`, seq = 세션 JSONL 절대 줄 번호 = 메시지 DOM 의 data-msg-seq)에서 최대 seq.
function _wicAnchorSeq(contextWindow: Record<string, unknown> | null, chatId: string | undefined): number | undefined {
  var refs = contextWindow ? contextWindow.sourceRefs : null;
  if (!chatId || !Array.isArray(refs)) return undefined;
  var prefix = chatId + "#msg_";
  var anchor: number | undefined;
  refs.forEach(function (ref: unknown) {
    if (typeof ref !== "string" || ref.indexOf(prefix) !== 0 || !/^\d+$/.test(ref.slice(prefix.length))) return;
    var seq = Number(ref.slice(prefix.length));
    if (anchor === undefined || seq > anchor) anchor = seq;
  });
  return anchor;
}

// 서버 outbox 레코드를 카드에 필요한 필드만 골라 평평하게 파싱한다.
function _parseWicRecord(value: unknown): WorkCandidateRecord | null {
  if (!_isWicRecord(value)) return null;
  var item = _wicObjAt(value, "item");
  var id = _wicStrAt(item, "id");
  var state = _wicStrAt(item, "state");
  if (!item || !id || !state) return null;
  var confidence = _wicObjAt(item, "confidence");
  var contextWindow = _wicObjAt(item, "contextWindow");
  var draft = _wicObjAt(value, "proposalDraft");
  var proposal = _wicObjAt(draft, "proposal");
  var conversion = _wicObjAt(value, "conversion");
  var registration = _wicObjAt(conversion, "registration");
  var revision = value.revision;
  return {
    id: id,
    state: state,
    revision: typeof revision === "number" ? revision : undefined,
    band: _wicStrAt(confidence, "band"),
    chatId: _wicStrAt(contextWindow, "chatId"),
    anchorSeq: _wicAnchorSeq(contextWindow, _wicStrAt(contextWindow, "chatId")),
    excerpt: _wicStrAt(contextWindow, "excerpt"),
    createdAt: _wicStrAt(item, "createdAt"),
    proposalId: _wicStrAt(item, "proposalId"),
    workItemId: _wicStrAt(item, "workItemId") || _wicStrAt(registration, "workItemId"),
    title: _wicStrAt(proposal, "title") || "제목 없는 후보",
    summary: _wicStrAt(proposal, "summary"),
    assignee: _wicActorRef(_wicObjAt(draft, "assignee")),
    owner: _wicActorRef(_wicObjAt(draft, "owner")),
    target: _wicTarget(_wicObjAt(_wicObjAt(value, "detection"), "target")),
    expiresAt: _wicStrAt(value, "expiresAt"),
    expiringSoon: value.expiringSoon === true,
    expiredAt: _wicStrAt(_wicObjAt(value, "expiry"), "expiredAt"),
    dismissedBy: _actorLabel(_wicObjAt(value, "dismissal") ? (_wicObjAt(value, "dismissal") as Record<string, unknown>).actor : undefined),
    convertedBy: _actorLabel(conversion ? conversion.actor : undefined),
    registeredOwner: _wicStrAt(_wicObjAt(registration, "owner"), "label"),
    priority: _wicStrAt(draft, "priority"),
    dueAt: _wicStrAt(_wicObjAt(draft, "time"), "dueAt"),
    project: _wicProject(_wicObjAt(draft, "project")),
    completionHint: _wicCompletionHint(_wicObjAt(value, "completionHint") || _wicObjAt(item, "completionHint")),
    projectId: _wicStrAt(item, "projectId") || _wicStrAt(registration, "projectId"),
    supersededBy: _wicStrAt(value, "supersededBy"),
  };
}

var WorkInlineCards = (function () {
  var _CONFIDENCE_LABELS: Record<string, string> = { high: "확신 높음", medium: "확신 보통", low: "확신 낮음" };
  var _TARGET_KIND_LABELS: Record<string, string> = { subitem: "하위 업무", link: "링크 붙이기", file: "파일 붙이기" };
  // outbox-id -> 원문 제목. work-outbox-resolved 이벤트는 제목을 싣지 않으므로 펼친 카드를 만들 때 채워두고 접힐 때 정리한다.
  var _cardTitles: Record<string, string> = {};
  var _peoplePromise: Promise<WorkCandidatePerson[]> | null = null;
  var _itemTitles: Record<string, Promise<string | null>> = {};
  var _PRIORITY_ORDER = ["low", "medium", "high", "critical"];
  var _PRIORITY_LABELS: Record<string, string> = { low: "낮음", medium: "보통", high: "높음", critical: "긴급" };
  var _companyPromise: Promise<WorkCompanyCatalog | null> | null = null;
  // 카드 요소 → 네 칸의 처음 값(미리 채운 값/기본값)과 지금 값. [등록]은 바뀐 칸만 보낸다(손대지 않은 칸은 서버가 같은 기본값을 쓴다).
  var _fieldStates = new WeakMap<HTMLElement, { initial: WorkCandidateFieldState; current: WorkCandidateFieldState }>();

  function _escape(str: unknown): string {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function _apiOutbox(): string { return "/api/work/outbox"; }
  function _apiOutboxItem(id: string): string { return "/api/work/outbox/" + encodeURIComponent(id); }

  // 서버 오류 코드와 HTTP 상태를 함께 넘긴다 — 409(이미 처리됨 / 다른 요청이 처리 중 / 후보가 바뀜) 수렴 판정에 쓴다.
  async function _fetchJson(url: string, options?: RequestInit): Promise<Record<string, unknown>> {
    var response = await fetch(url, options);
    var payload: unknown = await response.json().catch(function () { return {}; });
    if (!_isWicRecord(payload)) throw new Error("Invalid JSON response");
    if (!response.ok || payload.ok === false) {
      var message = typeof payload.message === "string" ? payload.message : (typeof payload.error === "string" ? payload.error : "Request failed: " + response.status);
      var requestError = new Error(message) as Error & { statusCode?: number; code?: string };
      requestError.statusCode = response.status;
      requestError.code = typeof payload.error === "string" ? payload.error : undefined;
      (requestError as Error & { payload?: Record<string, unknown> }).payload = payload;
      throw requestError;
    }
    return payload;
  }

  function _statusOf(error: unknown): number | undefined {
    return error instanceof Error ? (error as Error & { statusCode?: number }).statusCode : undefined;
  }

  function _codeOf(error: unknown): string | undefined {
    return error instanceof Error ? (error as Error & { code?: string }).code : undefined;
  }

  function _payloadOf(error: unknown): Record<string, unknown> | null {
    var payload = error instanceof Error ? (error as Error & { payload?: unknown }).payload : undefined;
    return _isWicRecord(payload) ? payload : null;
  }

  function _toast(message: string): void {
    if (typeof showSummaryToast === "function") showSummaryToast(message, true);
  }

  // features/*.ts는 views/chat.ts보다 먼저 로드되므로 #chatMessages를 호출 시점에 직접 조회한다.
  function _chatMessagesEl(): HTMLElement | null {
    return document.getElementById("chatMessages");
  }

  function _newOperationId(): string {
    var cryptoRef = typeof crypto !== "undefined" ? crypto : null;
    if (cryptoRef && typeof cryptoRef.randomUUID === "function") return "register-" + cryptoRef.randomUUID();
    return "register-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  function _viewerId(): string | null {
    return typeof viewerActorId === "function" ? viewerActorId() : null;
  }

  // 팀원 목록(GET /api/rooms/actors) — 등록 담당은 사람(user:*)만 받으므로 사람만 고른다. 실패하면 빈 목록(본인/감지 담당만).
  function _loadPeople(): Promise<WorkCandidatePerson[]> {
    if (_peoplePromise) return _peoplePromise;
    _peoplePromise = (async function () {
      try {
        var data = await _fetchJson("/api/rooms/actors");
        var users = Array.isArray(data.users) ? data.users : [];
        var people: WorkCandidatePerson[] = [];
        users.forEach(function (user: unknown) {
          if (!_isWicRecord(user)) return;
          var actorId = _wicStrAt(user, "actorId");
          if (!actorId || actorId.indexOf("user:") !== 0) return;
          var label = (_wicStrAt(user, "displayName") || "").trim() || actorId;
          people.push({ actorId: actorId, label: label, isSelf: user.isSelf === true });
        });
        return people.sort(function (a, b) { return Number(b.isSelf) - Number(a.isSelf) || a.label.localeCompare(b.label, "ko"); });
      } catch (error) {
        _peoplePromise = null;
        console.error("[work-inline-cards] 팀원 목록 로드 실패:", error instanceof Error ? error.message : String(error));
        return [];
      }
    })();
    return _peoplePromise;
  }

  // 붙이기 대상 업무의 제목(GET /api/work/items/:id). 감지가 진행 중 업무 목록에서 고른 id 라 대개 존재한다.
  function _loadItemTitle(itemId: string): Promise<string | null> {
    if (!_itemTitles[itemId]) {
      _itemTitles[itemId] = _fetchJson("/api/work/items/" + encodeURIComponent(itemId))
        .then(function (data) { return _wicStrAt(_wicObjAt(data, "item"), "title") || null; })
        .catch(function () { delete _itemTitles[itemId]; return null; });
    }
    return _itemTitles[itemId];
  }

  function _defaultOwnerId(record: WorkCandidateRecord): string | null {
    var assigneeId = record.assignee && record.assignee.actorId;
    if (assigneeId && assigneeId.indexOf("user:") === 0) return assigneeId;
    var ownerId = record.owner && record.owner.actorId;
    if (ownerId && ownerId.indexOf("user:") === 0) return ownerId;
    return _viewerId();
  }

  // "3일 뒤 만료" / 24시간 안이면 "N시간 뒤 만료"(expiringSoon). 상대 표기라 로케일 날짜 포맷에 기대지 않는다.
  function _expiryLabel(record: WorkCandidateRecord): string {
    if (!record.expiresAt) return "";
    var ms = Date.parse(record.expiresAt) - Date.now();
    if (Number.isNaN(ms)) return "";
    if (ms <= 0) return "곧 만료";
    var hours = Math.floor(ms / 3600000);
    if (hours < 1) return "1시간 안에 만료";
    if (hours < 24) return hours + "시간 뒤 만료";
    return Math.ceil(ms / 86400000) + "일 뒤 만료";
  }

  function _detectedLine(record: WorkCandidateRecord): string {
    var text = String(record.summary || record.excerpt || "").replace(/\s+/g, " ").trim();
    if (!text) return "대화에서 업무로 보이는 요청을 찾았어요.";
    return text.length > 140 ? text.slice(0, 137) + "…" : text;
  }

  function _resultText(record: WorkCandidateRecord, resolvedByLabel?: string): string {
    if (record.state === "converted") {
      if (record.projectId) return "프로젝트로 등록됨";
      if (record.workItemId) {
        var owner = record.registeredOwner;
        return "업무로 등록됨" + (owner ? " · 담당 " + owner : "");
      }
      // 옛 흐름(검토 문서 전환)으로 처리된 레코드 — 실제로 검토 문서가 만들어졌으므로 그대로 알린다.
      return "검토 문서로 넘어감" + ((resolvedByLabel || record.convertedBy) ? " · " + (resolvedByLabel || record.convertedBy) : "");
    }
    if (record.state === "dismissed") {
      if (record.supersededBy) return "더 최신 후보로 바뀌었어요";
      var by = resolvedByLabel || record.dismissedBy;
      return "무시됨" + (by ? " · " + by : "");
    }
    if (record.state === "expired") return "만료됨 · 7일 동안 처리되지 않았어요";
    return "";
  }

  // ===== 카드 네 칸(우선순위·마감·협업자·프로젝트, F20 확장) =====

  function _pad(value: number): string {
    return String(value).padStart(2, "0");
  }

  // ISO → date 입력 값(현지 날짜). 보드 상세(_boardDateInputValue)와 같은 규칙.
  function _dateInputValue(iso: string | null | undefined): string {
    if (!iso) return "";
    var date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.getFullYear() + "-" + _pad(date.getMonth() + 1) + "-" + _pad(date.getDate());
  }

  // date 입력 → ISO. 미리 채운 마감 시각이 있으면 그 시각을 살리고, 없으면 그날 18:00(현지) — 보드 상세(_boardDateToIso)와 같다.
  function _dateToIso(value: string, previousIso: string | null): string | null {
    var match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    var previous = previousIso ? new Date(previousIso) : null;
    var valid = !!previous && !Number.isNaN(previous.getTime());
    var hours = previous && valid ? previous.getHours() : 18;
    var minutes = previous && valid ? previous.getMinutes() : 0;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), hours, minutes).toISOString();
  }

  function _dueLabel(due: string): string {
    var match = due.match(/^\d{4}-(\d{2})-(\d{2})$/);
    return match ? "마감 " + Number(match[1]) + "월 " + Number(match[2]) + "일" : "마감 없음";
  }

  function _initialFieldState(record: WorkCandidateRecord): WorkCandidateFieldState {
    var priority = record.priority && _PRIORITY_LABELS[record.priority] ? record.priority : "medium";
    return {
      priority: priority,
      due: _dateInputValue(record.dueAt),
      dueIso: record.dueAt || null,
      collaborators: [],
      projectId: record.project ? record.project.id : null,
      projectLabel: record.project ? record.project.title : null,
      parentId: null,
      parentLabel: null,
    };
  }

  function _fieldState(el: HTMLElement, record: WorkCandidateRecord): { initial: WorkCandidateFieldState; current: WorkCandidateFieldState } {
    var state = _fieldStates.get(el);
    if (!state) {
      var initial = _initialFieldState(record);
      state = { initial: initial, current: { ...initial, collaborators: [] } };
      _fieldStates.set(el, state);
    }
    return state;
  }

  // 현재 값 한 줄: "보통 · 마감 없음 · 프로젝트 없음"(협업자가 있으면 " · 협업 N명").
  function _fieldSummaryText(state: WorkCandidateFieldState): string {
    var parts = [_PRIORITY_LABELS[state.priority] || "보통", _dueLabel(state.due), state.projectId ? (state.projectLabel || "프로젝트") : "프로젝트 없음"];
    if (state.collaborators.length) parts.push("협업 " + state.collaborators.length + "명");
    if (state.parentId) parts.push("↳ " + (state.parentLabel || "상위 업무"));
    return parts.join(" · ");
  }

  function _priorityOptionsHtml(selected: string): string {
    return _PRIORITY_ORDER.map(function (priority) {
      return '<option value="' + priority + '"' + (priority === selected ? " selected" : "") + '>' + _escape(_PRIORITY_LABELS[priority]) + '</option>';
    }).join("");
  }

  function _fieldErrorSlot(field: WorkCandidateFieldName): string {
    return '<span class="work-inline-card-field-error" data-field="' + field + '" role="alert" hidden></span>';
  }

  // 현재 값 한 줄(누르면 펼침) + 네 칸. 링크·파일 붙이기 대상이면 통째로 감춘다(_syncOwnerVisibility).
  // withParent: 채팅 `/task` 생성 카드는 상위 업무 칸을 더한다(후보 카드는 대상 칸의 "하위 업무로…"가 같은 일을 한다).
  function _moreFieldsHtml(state: WorkCandidateFieldState, withParent?: boolean): string {
    return '<div class="work-inline-card-more">' +
      '<button type="button" class="work-inline-card-more-toggle" aria-expanded="false">' +
        '<span class="work-inline-card-more-summary">' + _escape(_fieldSummaryText(state)) + '</span>' +
        '<span class="work-inline-card-more-chevron" aria-hidden="true"></span>' +
      '</button>' +
      '<p class="work-inline-card-project-note" hidden></p>' +
      '<div class="work-inline-card-more-fields" hidden>' +
        '<label class="work-inline-card-field work-inline-card-field--priority"><span class="work-inline-card-field-label">우선순위</span>' +
          '<select class="work-inline-card-priority" aria-label="우선순위">' + _priorityOptionsHtml(state.priority) + '</select>' + _fieldErrorSlot("priority") + '</label>' +
        '<label class="work-inline-card-field work-inline-card-field--due"><span class="work-inline-card-field-label">마감</span>' +
          '<input type="date" class="work-inline-card-due" aria-label="마감" value="' + _escape(state.due) + '">' +
          '<button type="button" class="work-inline-card-due-clear"' + (state.due ? '' : ' hidden') + '>지우기</button>' + _fieldErrorSlot("dueAt") + '</label>' +
        '<div class="work-inline-card-field work-inline-card-field--collaborators"><span class="work-inline-card-field-label">협업자</span>' +
          '<span class="work-inline-card-collaborators"></span>' +
          '<select class="work-inline-card-collab-add" aria-label="협업자 추가"><option value="">추가…</option></select>' + _fieldErrorSlot("collaborators") + '</div>' +
        '<label class="work-inline-card-field work-inline-card-field--project"><span class="work-inline-card-field-label">프로젝트</span>' +
          '<select class="work-inline-card-project" aria-label="프로젝트">' + _projectOptionsHtml(null, state) + '</select>' + _fieldErrorSlot("projectId") + '</label>' +
        (withParent ? '<label class="work-inline-card-field work-inline-card-field--parent"><span class="work-inline-card-field-label">상위 업무</span>' +
          '<select class="work-inline-card-parent" aria-label="상위 업무">' + _parentOptionsHtml(null, state) + '</select></label>' : '') +
      '</div>' +
    '</div>';
  }

  // 회사 md 목록(GET /api/company — 보드 상세 _boardLoadProjects 와 같은 원천): 프로젝트(업무 칸), 고객·트랙(프로젝트 칸).
  // 회사 md 가 없거나 깨졌으면 null(서버도 프로젝트를 비운다). 요청 자체가 실패하면 reject 하고 캐시를 비워 다음 호출이 다시 읽는다.
  function _loadCompany(): Promise<WorkCompanyCatalog | null> {
    if (_companyPromise) return _companyPromise;
    _companyPromise = (async function () {
      try {
        var data = await _fetchJson("/api/company");
        var fields = _wicObjAt(_wicObjAt(data, "company"), "fields");
        var projects = fields && Array.isArray(fields.projects) ? fields.projects : null;
        if (!fields || !projects) return null;
        var pick = function (list: unknown, key: string, labelKey: string): Array<{ id: string; name: string }> {
          var out: Array<{ id: string; name: string }> = [];
          (Array.isArray(list) ? list : []).forEach(function (entry: unknown) {
            if (!_isWicRecord(entry)) return;
            var id = _wicStrAt(entry, key);
            if (id) out.push({ id: id, name: _wicStrAt(entry, labelKey) || id });
          });
          return out;
        };
        var tracks = pick(data.tracks, "key", "label").map(function (track) { return { key: track.id, label: track.name }; });
        return { projects: pick(projects, "id", "name"), clients: pick(fields.clients, "id", "name"), tracks: tracks };
      } catch (error) {
        _companyPromise = null;
        console.error("[work-inline-cards] 회사 목록 로드 실패:", error instanceof Error ? error.message : String(error));
        throw error;
      }
    })();
    return _companyPromise;
  }

  function _loadProjects(): Promise<Array<{ id: string; name: string }> | null> {
    return _loadCompany().then(function (company) { return company ? company.projects : null; });
  }

  // 프로젝트 선택지: 프로젝트 없음 · 회사 md 프로젝트 · (미리 채운 값이 목록에 없으면) 그 값.
  function _projectOptionsHtml(projects: Array<{ id: string; name: string }> | null, state: WorkCandidateFieldState): string {
    var options = '<option value="">프로젝트 없음</option>';
    var list = (projects || []).slice();
    if (state.projectId && !list.some(function (project) { return project.id === state.projectId; })) {
      list.push({ id: state.projectId, name: state.projectLabel || state.projectId });
    }
    list.forEach(function (project) {
      options += '<option value="' + _escape(project.id) + '"' + (project.id === state.projectId ? " selected" : "") + '>' + _escape(project.name) + '</option>';
    });
    return options;
  }

  // 상위 업무 선택지: 없음 + 후보(보조 글 = 프로젝트 · 담당). 프로젝트를 골라 두면 그 프로젝트 업무를 "이 프로젝트" 묶음으로 위에.
  function _parentOptionsHtml(parents: WorkParentCandidate[] | null, state: WorkCandidateFieldState): string {
    var option = function (parent: WorkParentCandidate): string {
      var meta = (parent.projectTitle || "프로젝트 없음") + (parent.ownerLabel ? " · " + parent.ownerLabel : "");
      return '<option value="' + _escape(parent.id) + '" data-meta="' + _escape(meta) + '"' + (parent.id === state.parentId ? " selected" : "") + '>' + _escape(parent.title) + '</option>';
    };
    var list = (parents || []).slice();
    if (state.parentId && !list.some(function (parent) { return parent.id === state.parentId; })) {
      list.push({ id: state.parentId, title: state.parentLabel || state.parentId, projectId: null, projectTitle: null, ownerLabel: null });
    }
    var html = '<option value="">없음(새 업무)</option>';
    var mine = state.projectId ? list.filter(function (parent) { return parent.projectId === state.projectId; }) : [];
    if (!mine.length) return html + list.map(option).join("");
    var rest = list.filter(function (parent) { return mine.indexOf(parent) === -1; });
    html += '<optgroup label="이 프로젝트">' + mine.map(option).join("") + '</optgroup>';
    if (rest.length) html += '<optgroup label="다른 업무">' + rest.map(option).join("") + '</optgroup>';
    return html;
  }

  // 상위 업무 목록은 펼칠 때 한 번 읽는다(실패하면 다음 펼침에서 다시). 프로젝트를 바꾸면 읽어 둔 목록으로 묶음만 다시 짠다.
  var _parentLists = new WeakMap<HTMLElement, WorkParentCandidate[]>();
  function _renderParentOptions(el: HTMLElement): void {
    var select = el.querySelector<HTMLSelectElement>(".work-inline-card-parent");
    var state = _fieldStates.get(el);
    if (!select || !state) return;
    select.innerHTML = _parentOptionsHtml(_parentLists.get(el) || null, state.current);
  }

  function _syncParents(el: HTMLElement): void {
    if (!el.querySelector(".work-inline-card-parent")) return;
    if (el.dataset.parentsLoaded === "true" || el.dataset.parentsLoaded === "loading") return;
    el.dataset.parentsLoaded = "loading";
    void _loadActiveParents().then(function (parents) {
      if (el.getAttribute("data-state") !== "pending_review") return;
      el.dataset.parentsLoaded = "true";
      _parentLists.set(el, parents);
      _renderParentOptions(el);
    }).catch(function () {
      if (el.dataset.parentsLoaded === "loading") delete el.dataset.parentsLoaded;
    });
  }

  function _refreshFieldSummary(el: HTMLElement, state: WorkCandidateFieldState): void {
    var summary = el.querySelector<HTMLElement>(".work-inline-card-more-summary");
    if (summary) summary.textContent = _fieldSummaryText(state);
  }

  function _ownerValue(el: HTMLElement): string {
    var owner = el.querySelector<HTMLSelectElement>(".work-inline-card-owner");
    return owner ? owner.value : "";
  }

  // 협업자 칩(이름 + 빼기)과 추가 선택지(사람만, 이미 넣은 사람·지금 담당 제외 — 서버도 담당은 협업자에서 뺀다).
  function _renderCollaborators(el: HTMLElement, state: WorkCandidateFieldState, people: WorkCandidatePerson[]): void {
    var chips = el.querySelector<HTMLElement>(".work-inline-card-collaborators");
    var add = el.querySelector<HTMLSelectElement>(".work-inline-card-collab-add");
    var ownerId = _ownerValue(el);
    if (chips) {
      var chipHost: HTMLElement = chips;
      chipHost.textContent = "";
      state.collaborators.forEach(function (person) {
        var chip = document.createElement("span");
        chip.className = "work-inline-card-collab-chip";
        chip.dataset.actorId = person.actorId;
        var name = document.createElement("span");
        name.textContent = person.label;
        var remove = document.createElement("button");
        remove.type = "button";
        remove.className = "work-inline-card-collab-remove";
        remove.setAttribute("aria-label", person.label + " 빼기");
        remove.textContent = "×";
        remove.addEventListener("click", function () {
          state.collaborators = state.collaborators.filter(function (entry) { return entry.actorId !== person.actorId; });
          _renderCollaborators(el, state, people);
          _refreshFieldSummary(el, state);
          _setFieldError(el, "collaborators", "");
        });
        chip.appendChild(name);
        chip.appendChild(remove);
        chipHost.appendChild(chip);
      });
    }
    if (add) {
      var html = '<option value="">추가…</option>';
      people.forEach(function (person) {
        if (person.actorId === ownerId || state.collaborators.some(function (entry) { return entry.actorId === person.actorId; })) return;
        html += '<option value="' + _escape(person.actorId) + '">' + _escape(person.isSelf ? person.label + " (나)" : person.label) + '</option>';
      });
      add.innerHTML = html;
    }
  }

  function _setFieldError(el: HTMLElement, field: WorkCandidateFieldName, message: string): void {
    var slot = el.querySelector<HTMLElement>('.work-inline-card-field-error[data-field="' + field + '"]');
    if (!slot) return;
    slot.textContent = message;
    slot.hidden = !message;
  }

  function _clearFieldErrors(el: HTMLElement): void {
    (["priority", "dueAt", "collaborators", "projectId"] as WorkCandidateFieldName[]).forEach(function (field) { _setFieldError(el, field, ""); });
  }

  function _setMoreOpen(el: HTMLElement, open: boolean): void {
    var toggle = el.querySelector<HTMLButtonElement>(".work-inline-card-more-toggle");
    var panel = el.querySelector<HTMLElement>(".work-inline-card-more-fields");
    if (!toggle || !panel) return;
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    panel.hidden = !open;
    // 프로젝트 목록은 펼칠 때 읽는다(카드마다 회사 md 를 읽지 않게). 실패하면 다음 펼침에서 다시 읽는다.
    if (open) { _syncProjects(el); _syncParents(el); }
  }

  // 회사 프로젝트 목록을 읽어 선택지를 채우고, 미리 채운 프로젝트를 서버와 같은 기준으로 맞춘다: 목록에 있으면 회사 md
  // 이름으로, 없으면(또는 회사 md 가 없거나 깨졌으면) 서버가 비우므로 "프로젝트 없음"으로 보이고 짧게 알린다.
  function _syncProjects(el: HTMLElement): void {
    if (el.dataset.projectsLoaded === "true" || el.dataset.projectsLoaded === "loading") return;
    el.dataset.projectsLoaded = "loading";
    void _loadProjects().then(function (projects) {
      var state = _fieldStates.get(el);
      if (!state || el.getAttribute("data-state") !== "pending_review") return;
      el.dataset.projectsLoaded = "true";
      var initial = state.initial;
      var current = state.current;
      if (initial.projectId) {
        var draftId = initial.projectId;
        var match = (projects || []).filter(function (project) { return project.id === draftId; })[0];
        if (match) {
          initial.projectLabel = match.name;
          if (current.projectId === draftId) current.projectLabel = match.name;
        } else {
          var draftLabel = initial.projectLabel || draftId;
          initial.projectId = null;
          initial.projectLabel = null;
          if (current.projectId === draftId) { current.projectId = null; current.projectLabel = null; }
          var note = el.querySelector<HTMLElement>(".work-inline-card-project-note");
          if (note) {
            note.textContent = "미리 채운 프로젝트 '" + draftLabel + "'는 회사 목록에 없어 비워져요.";
            note.hidden = false;
          }
        }
        _refreshFieldSummary(el, current);
      }
      var select = el.querySelector<HTMLSelectElement>(".work-inline-card-project");
      if (select) select.innerHTML = _projectOptionsHtml(projects, current);
    }).catch(function () {
      if (el.dataset.projectsLoaded === "loading") delete el.dataset.projectsLoaded;
    });
  }

  function _wireMoreFields(el: HTMLElement, record: WorkCandidateRecord): void {
    var state = _fieldState(el, record).current;
    var toggle = el.querySelector<HTMLButtonElement>(".work-inline-card-more-toggle");
    if (toggle) {
      var toggleBtn: HTMLButtonElement = toggle;
      toggleBtn.addEventListener("click", function () { _setMoreOpen(el, toggleBtn.getAttribute("aria-expanded") !== "true"); });
    }
    var priority = el.querySelector<HTMLSelectElement>(".work-inline-card-priority");
    if (priority) {
      var prioritySelect: HTMLSelectElement = priority;
      prioritySelect.addEventListener("change", function () {
        state.priority = prioritySelect.value;
        _refreshFieldSummary(el, state);
        _setFieldError(el, "priority", "");
      });
    }
    var due = el.querySelector<HTMLInputElement>(".work-inline-card-due");
    var dueClear = el.querySelector<HTMLButtonElement>(".work-inline-card-due-clear");
    var syncDue = function (value: string) {
      state.due = value;
      if (dueClear) dueClear.hidden = !value;
      _refreshFieldSummary(el, state);
      _setFieldError(el, "dueAt", "");
    };
    if (due) {
      var dueInput: HTMLInputElement = due;
      dueInput.addEventListener("change", function () { syncDue(dueInput.value); });
    }
    if (dueClear) dueClear.addEventListener("click", function () {
      if (due) due.value = "";
      syncDue("");
    });
    var project = el.querySelector<HTMLSelectElement>(".work-inline-card-project");
    if (project) {
      var projectSelect: HTMLSelectElement = project;
      projectSelect.addEventListener("change", function () {
        var picked = projectSelect.options[projectSelect.selectedIndex];
        state.projectId = projectSelect.value || null;
        state.projectLabel = projectSelect.value ? ((picked && picked.textContent) || projectSelect.value) : null;
        _refreshFieldSummary(el, state);
        _setFieldError(el, "projectId", "");
        _renderParentOptions(el); // "이 프로젝트" 묶음을 다시 짠다(고른 상위 업무는 그대로)
      });
      // 프로젝트는 검색 드롭다운(core/search-select.ts, 카드 크기). 값의 주인은 그대로 이 select 다.
      if (typeof SearchSelect !== "undefined") SearchSelect.attach(projectSelect, { size: "sm", searchPlaceholder: "프로젝트 검색", emptyText: "맞는 프로젝트가 없어요." });
    }
    var parent = el.querySelector<HTMLSelectElement>(".work-inline-card-parent");
    if (parent) {
      var parentSelect: HTMLSelectElement = parent;
      parentSelect.addEventListener("change", function () {
        var picked = parentSelect.options[parentSelect.selectedIndex];
        state.parentId = parentSelect.value || null;
        state.parentLabel = parentSelect.value ? ((picked && picked.textContent) || parentSelect.value) : null;
        _refreshFieldSummary(el, state);
      });
      if (typeof SearchSelect !== "undefined") SearchSelect.attach(parentSelect, { size: "sm", searchPlaceholder: "업무 제목·프로젝트·담당으로 검색", emptyText: "맞는 업무가 없어요." });
    }
    var people: WorkCandidatePerson[] = [];
    var add = el.querySelector<HTMLSelectElement>(".work-inline-card-collab-add");
    if (add) {
      var addSelect: HTMLSelectElement = add;
      addSelect.addEventListener("change", function () {
        var actorId = addSelect.value;
        var person = people.filter(function (entry) { return entry.actorId === actorId; })[0];
        if (person && !state.collaborators.some(function (entry) { return entry.actorId === actorId; })) {
          state.collaborators.push({ actorId: person.actorId, label: person.label });
        }
        _renderCollaborators(el, state, people);
        _refreshFieldSummary(el, state);
        _setFieldError(el, "collaborators", "");
      });
    }
    var owner = el.querySelector<HTMLSelectElement>(".work-inline-card-owner");
    if (owner) {
      var ownerSelect: HTMLSelectElement = owner;
      ownerSelect.addEventListener("change", function () {
        // 담당으로 고른 사람은 협업자에서 뺀다(서버 규칙과 같다).
        state.collaborators = state.collaborators.filter(function (entry) { return entry.actorId !== ownerSelect.value; });
        _renderCollaborators(el, state, people);
        _refreshFieldSummary(el, state);
      });
    }
    void _loadPeople().then(function (loaded) {
      if (el.getAttribute("data-state") !== "pending_review") return;
      people = loaded;
      _renderCollaborators(el, state, people);
    });
  }

  // [등록] body 에 실을 네 칸: 처음 값(미리 채운 값/기본값)에서 바뀐 칸만. 손대지 않은 칸은 서버가 같은 값을 쓴다 —
  // 미리 채운 프로젝트가 회사 md 목록에서 빠졌어도 사람이 고르지 않았으면 422 가 되지 않는다.
  function _changedFields(el: HTMLElement, record: WorkCandidateRecord): Record<string, unknown> {
    var state = _fieldState(el, record);
    var out: Record<string, unknown> = {};
    if (state.current.priority !== state.initial.priority) out.priority = state.current.priority;
    if (state.current.due !== state.initial.due) out.dueAt = state.current.due ? _dateToIso(state.current.due, state.initial.dueIso) : null;
    if (state.current.projectId !== state.initial.projectId) out.projectId = state.current.projectId;
    if (state.current.collaborators.length) out.collaborators = state.current.collaborators.map(function (entry) { return entry.actorId; });
    return out;
  }

  // 422·400 오류를 해당 칸 옆에도 보인다(카드 아래 오류 줄은 그대로). 네 칸이 접혀 있으면 펼친다.
  function _showFieldError(el: HTMLElement, record: WorkCandidateRecord, error: unknown): void {
    var code = _codeOf(error);
    var payload = _payloadOf(error);
    var field: WorkCandidateFieldName | null = null;
    var message = "";
    if (code === "unknown_project" || code === "company_record_unavailable" || code === "project_deleted") {
      field = "projectId";
      // project_deleted(409, r3b 검토 반영): 고른 프로젝트가 그사이 삭제됐다.
      var deletedName = payload ? _wicStrAt(payload, "projectName") : undefined;
      message = code === "project_deleted" ? "그 사이 프로젝트" + (deletedName ? " \"" + deletedName + "\"" : "") + "가 삭제됐어요. 다른 프로젝트를 고르거나 \"프로젝트 없음\"으로 두세요."
        : code === "unknown_project" ? "회사 프로젝트 목록에 없는 프로젝트예요." : "회사 정보가 없어 프로젝트를 고를 수 없어요.";
    } else if (code === "work_actor_unknown" || code === "work_actor_unverifiable") {
      var actor = payload ? _wicStrAt(payload, "actor") : undefined;
      if (actor && _fieldState(el, record).current.collaborators.some(function (entry) { return entry.actorId === actor; })) {
        field = "collaborators";
        message = "팀원으로 확인되지 않는 사람이 있어요.";
      }
    } else if (code === "invalid_work_item_patch") {
      var issues: string[] = payload && Array.isArray(payload.issues) ? payload.issues.map(String) : [];
      var names: WorkCandidateFieldName[] = ["priority", "dueAt", "collaborators", "projectId"];
      var issueField = names.filter(function (name) {
        return issues.some(function (issue) { return issue.indexOf(name) === 0; });
      })[0];
      if (issueField) {
        field = issueField;
        message = "값을 확인해 주세요.";
      }
    }
    if (!field) return;
    _setMoreOpen(el, true);
    _setFieldError(el, field, message);
  }

  // 감지가 "끝난 것 같아요"로 본 근거(completionHint). 자동 무시는 없다 — 사람이 [무시]를 누른다.
  function _completionHintHtml(record: WorkCandidateRecord): string {
    var hint = record.completionHint;
    if (!hint) return "";
    return '<div class="work-inline-card-hint">' +
      '<span class="work-inline-card-hint-label">끝난 것 같아요</span>' +
      (hint.reason ? '<span class="work-inline-card-hint-reason">' + _escape(hint.reason) + '</span>' : '') +
      (hint.excerpt ? '<details class="work-inline-card-hint-evidence"><summary>근거 보기</summary><blockquote>' + _escape(hint.excerpt) + '</blockquote></details>' : '') +
    '</div>';
  }

  // ===== 카드 렌더 =====

  function _applyCollapsedContent(el: HTMLElement, record: WorkCandidateRecord, resolvedByLabel?: string): void {
    el.className = "work-inline-card work-inline-card--collapsed";
    el.setAttribute("data-outbox-id", record.id);
    el.setAttribute("data-state", record.state);
    el.innerHTML =
      '<span class="work-inline-card-dot" data-state="' + _escape(record.state) + '" aria-hidden="true"></span>' +
      '<span class="work-inline-card-collapsed-title">' + _escape(record.title) + '</span>' +
      '<span class="work-inline-card-collapsed-text">' + _escape(_resultText(record, resolvedByLabel)) + '</span>';
    delete _cardTitles[record.id];
    if (record.state === "converted" && record.projectId) {
      el.appendChild(_projectBoardLink(record.projectId));
      return;
    }
    if (record.state === "dismissed" && record.supersededBy) {
      var newerId = record.supersededBy;
      var open = document.createElement("button");
      open.type = "button";
      open.className = "work-inline-card-board-link work-inline-card-newer-link";
      open.textContent = "새 카드 열기";
      open.addEventListener("click", function () { _openNewerCard(newerId); });
      el.appendChild(open);
      return;
    }
    if (record.state === "converted" && record.workItemId) {
      var workItemId = record.workItemId;
      var link = document.createElement("button");
      link.type = "button";
      link.className = "work-inline-card-board-link";
      link.textContent = "보드에서 보기";
      link.addEventListener("click", function () {
        if (typeof router !== "undefined") router.navigate("work", { params: { itemId: workItemId } });
      });
      el.appendChild(link);
    }
  }

  // 보드 프로젝트 영역 딥링크(#work?project=<id>, 운영표 알림과 같은 경로).
  function _projectBoardLink(projectId: string): HTMLButtonElement {
    var link = document.createElement("button");
    link.type = "button";
    link.className = "work-inline-card-board-link";
    link.textContent = "보드에서 열기";
    link.addEventListener("click", function () {
      if (typeof router !== "undefined") router.navigate("work", { params: { project: projectId } });
    });
    return link;
  }

  // 대체한 새 후보 카드로 간다: 같은 화면(채팅·후보함·배지)에 있으면 그 카드로 스크롤해 제목에 포커스, 없으면 후보함.
  function _openNewerCard(outboxId: string): void {
    var cards = document.querySelectorAll<HTMLElement>(".work-inline-card");
    for (var i = 0; i < cards.length; i += 1) {
      if (cards[i].dataset.outboxId !== outboxId) continue;
      if (typeof cards[i].scrollIntoView === "function") cards[i].scrollIntoView({ block: "center" });
      var title = cards[i].querySelector<HTMLInputElement>(".work-inline-card-title-input");
      if (title) title.focus();
      return;
    }
    if (typeof router !== "undefined") router.navigate("work-outbox");
  }

  function _ownerOptionsHtml(people: WorkCandidatePerson[], selectedId: string | null, record: WorkCandidateRecord): string {
    var options = people.slice();
    // 감지·편집 담당이 팀원 목록에 없을 때(레지스트리 조회 실패 등)도 그 사람을 고를 수 있게 남긴다.
    [record.assignee, record.owner].forEach(function (ref) {
      if (ref && ref.actorId && ref.actorId.indexOf("user:") === 0 && !options.some(function (p) { return p.actorId === ref.actorId; })) {
        options.push({ actorId: ref.actorId, label: ref.label || "감지된 담당", isSelf: false });
      }
    });
    var viewer = _viewerId();
    if (viewer && !options.some(function (p) { return p.actorId === viewer; })) options.unshift({ actorId: viewer, label: "나", isSelf: true });
    return options.map(function (person) {
      var label = person.isSelf ? person.label + " (나)" : person.label;
      return '<option value="' + _escape(person.actorId) + '"' + (person.actorId === selectedId ? " selected" : "") + '>' + _escape(label) + '</option>';
    }).join("");
  }

  // 대상 선택지: 감지가 고른 붙이기 대상(있으면 기본) · 새 업무 · 사람이 고른 진행 중 업무의 하위 업무 · "다른 업무 고르기…".
  // 링크·파일 붙이기는 감지 근거(인용 메시지의 URL·첨부)가 있을 때만 나온다 — 사람이 고를 수 있는 건 하위 업무 대상뿐이다.
  function _targetOptionsHtml(el: HTMLElement, record: WorkCandidateRecord, detectedTitle: string | null, selected: string): string {
    var target = record.target;
    var options = "";
    if (target && target.type === "attach") {
      options += '<option value="detected">' + _escape(_TARGET_KIND_LABELS[target.kind] + " · " + (detectedTitle || "진행 중 업무")) + '</option>';
    }
    options += '<option value="new">새 업무</option>';
    var pickedId = el.dataset.pickedParentId;
    if (pickedId) options += '<option value="parent:' + _escape(pickedId) + '">' + _escape("하위 업무 · " + (el.dataset.pickedParentTitle || "고른 업무")) + '</option>';
    options += '<option value="pick">다른 업무의 하위 업무로…</option>';
    return options.replace('value="' + _escape(selected) + '"', 'value="' + _escape(selected) + '" selected');
  }

  function _defaultTargetValue(record: WorkCandidateRecord): string {
    return record.target && record.target.type === "attach" ? "detected" : "new";
  }

  function _selectedTarget(el: HTMLElement, record: WorkCandidateRecord): WorkCandidateTarget | null {
    var select = el.querySelector<HTMLSelectElement>(".work-inline-card-target");
    var value = select ? select.value : _defaultTargetValue(record);
    if (value === "detected" && record.target && record.target.type === "attach") return record.target;
    if (value.indexOf("parent:") === 0) return { type: "attach", workItemId: value.slice(7), kind: "subitem" };
    if (value === "pick") return null; // 아직 고르지 않았다
    return { type: "new" };
  }

  // 진행 중 업무 목록(보드 API 재사용): 하위 업무를 둘 수 있는 최상위 업무만(하위 업무는 하위를 가질 수 없다 — 깊이 1).
  function _loadActiveParents(): Promise<WorkParentCandidate[]> {
    return _fetchJson("/api/work/board?lifecycle=active&scopes=team").then(function (data) {
      var lanes = Array.isArray(data.lanes) ? data.lanes : [];
      var out: WorkParentCandidate[] = [];
      lanes.forEach(function (lane: unknown) {
        var cards = _isWicRecord(lane) && Array.isArray(lane.cards) ? lane.cards : [];
        cards.forEach(function (card: unknown) {
          if (!_isWicRecord(card)) return;
          var id = _wicStrAt(card, "id");
          var status = _wicStrAt(card, "status");
          if (!id || (typeof card.parentId === "string" && card.parentId) || status === "done" || status === "cancelled") return;
          var projectView = _wicObjAt(card, "projectView");
          out.push({
            id: id,
            title: _wicStrAt(card, "title") || id,
            projectId: projectView && _wicStrAt(projectView, "source") !== "deleted" ? _wicStrAt(projectView, "id") || null : null,
            projectTitle: projectView ? _wicStrAt(projectView, "title") || null : null,
            ownerLabel: _wicStrAt(_wicObjAt(card, "owner"), "label") || null,
          });
        });
      });
      return out;
    });
  }

  function _closeParentPicker(el: HTMLElement): void {
    var picker = el.querySelector<HTMLElement>(".work-inline-card-parent-picker");
    if (picker) picker.remove();
  }

  // 검색할 수 있는 진행 중 업무 고르기. 고르면 "하위 업무 · 제목" 선택지가 생기고 그 값이 선택된다.
  function _openParentPicker(el: HTMLElement, record: WorkCandidateRecord, previous: string): void {
    _closeParentPicker(el);
    var fields = el.querySelector<HTMLElement>(".work-inline-card-fields");
    if (!fields) return;
    var picker = document.createElement("div");
    picker.className = "work-inline-card-parent-picker";
    picker.innerHTML = '<input type="search" class="work-inline-card-parent-search" placeholder="진행 중 업무 검색" aria-label="하위 업무로 붙일 업무 검색">' +
      '<div class="work-inline-card-parent-results" role="listbox" aria-label="진행 중 업무"><div class="work-inline-card-parent-empty">불러오는 중…</div></div>' +
      '<button type="button" class="work-inline-card-parent-cancel">취소</button>';
    fields.insertAdjacentElement("afterend", picker);
    var search = picker.querySelector<HTMLInputElement>(".work-inline-card-parent-search");
    var resultsEl = picker.querySelector<HTMLElement>(".work-inline-card-parent-results");
    if (!resultsEl) return;
    var results: HTMLElement = resultsEl;
    var cancel = picker.querySelector<HTMLButtonElement>(".work-inline-card-parent-cancel");
    var items: WorkParentCandidate[] = [];
    var restore = function () {
      var select = el.querySelector<HTMLSelectElement>(".work-inline-card-target");
      if (select) select.value = previous === "pick" ? _defaultTargetValue(record) : previous;
      _closeParentPicker(el);
      _syncOwnerVisibility(el, record);
    };
    var render = function () {
      var q = (search ? search.value : "").trim().toLowerCase();
      var matches = items.filter(function (item) { return !q || item.title.toLowerCase().indexOf(q) !== -1; }).slice(0, 8);
      results.textContent = "";
      if (!matches.length) {
        var empty = document.createElement("div");
        empty.className = "work-inline-card-parent-empty";
        empty.textContent = items.length ? "맞는 업무가 없어요." : "진행 중인 업무가 없어요.";
        results.appendChild(empty);
        return;
      }
      matches.forEach(function (item) {
        var option = document.createElement("button");
        option.type = "button";
        option.className = "work-inline-card-parent-option";
        option.setAttribute("role", "option");
        option.dataset.itemId = item.id;
        option.textContent = item.title;
        option.addEventListener("click", function () {
          el.dataset.pickedParentId = item.id;
          el.dataset.pickedParentTitle = item.title;
          var select = el.querySelector<HTMLSelectElement>(".work-inline-card-target");
          if (select) select.innerHTML = _targetOptionsHtml(el, record, el.dataset.detectedTitle || null, "parent:" + item.id);
          _closeParentPicker(el);
          _syncOwnerVisibility(el, record);
          _setError(el, "");
        });
        results.appendChild(option);
      });
    };
    if (search) {
      search.addEventListener("input", render);
      search.addEventListener("keydown", function (event: KeyboardEvent) { if (event.key === "Escape") restore(); });
      search.focus();
    }
    if (cancel) cancel.addEventListener("click", restore);
    void _loadActiveParents().then(function (loaded) {
      items = loaded;
      render();
    }).catch(function () {
      results.innerHTML = '<div class="work-inline-card-parent-empty">진행 중 업무를 불러오지 못했어요.</div>';
    });
  }

  // 링크·파일 붙이기에는 담당이 쓰이지 않으므로(서버도 무시) 담당 칸을 감춘다. 우선순위·마감·협업자·프로젝트도
  // 기존 업무에 붙이는 대상에서는 쓸 곳이 없어(서버 400 register_fields_not_applicable) 현재 값 한 줄과 네 칸을 함께 감춘다.
  function _attachOnlyTarget(el: HTMLElement, record: WorkCandidateRecord): boolean {
    var target = _selectedTarget(el, record);
    return !!target && target.type === "attach" && target.kind !== "subitem";
  }

  function _syncOwnerVisibility(el: HTMLElement, record: WorkCandidateRecord): void {
    var project = el.dataset.kind === "project";
    var attachOnly = !project && _attachOnlyTarget(el, record);
    var ownerField = el.querySelector<HTMLElement>(".work-inline-card-field--owner");
    if (ownerField) ownerField.hidden = attachOnly;
    var fields = el.querySelector<HTMLElement>(".work-inline-card-fields");
    if (fields) fields.hidden = project;
    var more = el.querySelector<HTMLElement>(".work-inline-card-more");
    if (more) more.hidden = attachOnly || project;
    var projectFields = el.querySelector<HTMLElement>(".work-inline-card-project-fields");
    if (projectFields) projectFields.hidden = !project;
  }

  function _setError(el: HTMLElement, message: string): void {
    var slot = el.querySelector<HTMLElement>(".work-inline-card-error");
    if (!slot) return;
    slot.textContent = message;
    slot.hidden = !message;
  }

  function _setBusy(el: HTMLElement, busy: boolean, which?: "register" | "dismiss"): void {
    el.setAttribute("aria-busy", busy ? "true" : "false");
    el.querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLSelectElement>("button, input, select").forEach(function (control) {
      control.disabled = busy;
    });
    var registerBtn = el.querySelector<HTMLButtonElement>(".work-inline-card-register-btn");
    if (registerBtn) registerBtn.textContent = busy && which === "register" ? "등록 중…" : "등록";
  }

  function buildCard(rawOrRecord: unknown, options: WorkCandidateCardOptions): HTMLElement {
    var record = _isWicRecord(rawOrRecord) && typeof (rawOrRecord as WorkCandidateRecord).state === "string" && typeof (rawOrRecord as WorkCandidateRecord).title === "string"
      ? rawOrRecord as WorkCandidateRecord
      : _parseWicRecord(rawOrRecord);
    var el = document.createElement("article");
    if (!record) {
      el.className = "work-inline-card work-inline-card--collapsed";
      el.textContent = "후보를 읽지 못했어요.";
      return el;
    }
    el.dataset.context = options.context;
    if (record.state !== "pending_review") {
      _applyCollapsedContent(el, record);
      return el;
    }
    _renderExpanded(el, record, options);
    return el;
  }

  function _renderExpanded(el: HTMLElement, record: WorkCandidateRecord, options: WorkCandidateCardOptions): void {
    var bandLabel = record.band ? (_CONFIDENCE_LABELS[record.band] || "") : "";
    var expiry = _expiryLabel(record);
    var ownerId = _defaultOwnerId(record);
    el.className = "work-inline-card";
    el.setAttribute("data-outbox-id", record.id);
    el.setAttribute("data-state", record.state);
    el.setAttribute("aria-busy", "false");
    if (!el.dataset.operationId) el.dataset.operationId = _newOperationId();
    _cardTitles[record.id] = record.title;
    // 다시 그릴 때(409 수렴으로 최신 레코드)는 네 칸도 그 레코드의 미리 채운 값부터 다시 시작한다.
    _fieldStates.delete(el);
    delete el.dataset.projectsLoaded;
    var fieldState = _fieldState(el, record).current;
    el.innerHTML =
      '<div class="work-inline-card-head">' +
        '<span class="work-inline-card-kicker">업무 후보</span>' +
        (bandLabel ? '<span class="work-inline-card-confidence" data-band="' + _escape(record.band) + '">' + _escape(bandLabel) + '</span>' : '') +
        (expiry ? '<span class="work-inline-card-expiry"' + (record.expiringSoon ? ' data-soon="true"' : '') + '>' + _escape(expiry) + '</span>' : '') +
      '</div>' +
      '<p class="work-inline-card-detected">' + _escape(_detectedLine(record)) + '</p>' +
      _completionHintHtml(record) +
      _project.kindToggleHtml("task") +
      '<input class="work-inline-card-title-input" type="text" maxlength="300" aria-label="업무 제목" value="' + _escape(record.title) + '">' +
      '<div class="work-inline-card-fields">' +
        '<label class="work-inline-card-field work-inline-card-field--owner"><span class="work-inline-card-field-label">담당</span>' +
          '<select class="work-inline-card-owner" aria-label="담당">' + _ownerOptionsHtml([], ownerId, record) + '</select></label>' +
        '<label class="work-inline-card-field work-inline-card-field--target"><span class="work-inline-card-field-label">대상</span>' +
          '<select class="work-inline-card-target" aria-label="대상">' + _targetOptionsHtml(el, record, el.dataset.detectedTitle || null, _defaultTargetValue(record)) + '</select>' +
        '</label>' +
      '</div>' +
      _moreFieldsHtml(fieldState) +
      _project.projectFieldsHtml() +
      (record.excerpt ? '<details class="work-inline-card-evidence"><summary>' + (String(record.chatId || "").indexOf("meeting:") === 0 ? "근거 회의" : "근거 대화") + '</summary><blockquote>' + _escape(record.excerpt) + '</blockquote></details>' : '') +
      '<div class="work-inline-card-error" role="alert" hidden></div>' +
      '<div class="work-inline-card-actions">' +
        '<button type="button" class="work-inline-card-dismiss-btn">무시</button>' +
        '<button type="button" class="work-inline-card-register-btn">등록</button>' +
      '</div>';

    var registerBtn = el.querySelector<HTMLButtonElement>(".work-inline-card-register-btn");
    var dismissBtn = el.querySelector<HTMLButtonElement>(".work-inline-card-dismiss-btn");
    var titleInput = el.querySelector<HTMLInputElement>(".work-inline-card-title-input");
    var targetSelect = el.querySelector<HTMLSelectElement>(".work-inline-card-target");
    if (registerBtn) registerBtn.addEventListener("click", function () { void _registerCard(el, record, options); });
    if (dismissBtn) dismissBtn.addEventListener("click", function () { void _dismissCard(el, record, options); });
    if (titleInput) {
      titleInput.addEventListener("keydown", function (event: KeyboardEvent) {
        // 한글 IME 조합 중 Enter(isComposing / keyCode 229)는 확정 키라 등록으로 보지 않는다.
        if (event.key !== "Enter" || event.isComposing || event.keyCode === 229) return;
        event.preventDefault();
        void _registerCard(el, record, options);
      });
    }
    if (targetSelect) {
      var targetControl: HTMLSelectElement = targetSelect;
      var lastTarget = targetControl.value;
      targetControl.addEventListener("focus", function () { lastTarget = targetControl.value; });
      targetControl.addEventListener("change", function () {
        if (targetControl.value === "pick") _openParentPicker(el, record, lastTarget);
        else { _closeParentPicker(el); lastTarget = targetControl.value; }
        _syncOwnerVisibility(el, record);
      });
    }
    el.dataset.kind = "task";
    delete el.dataset.projectWired;
    delete el.dataset.extrasLoaded;
    _project.reset(el);
    _project.wireKindToggle(el, record);
    // 저장하지 않은 입력이 있는지(검토 #6): 대체 이벤트가 와도 입력 중인 카드는 접지 않고 배너만 띄운다.
    delete el.dataset.dirty;
    el.querySelector(".work-inline-card-newer-banner")?.remove();
    if (el.dataset.dirtyWired !== "true") {
      el.dataset.dirtyWired = "true";
      var markDirty = function () { el.dataset.dirty = "true"; };
      el.addEventListener("input", markDirty);
      el.addEventListener("change", markDirty);
      el.addEventListener("click", function (event: Event) {
        var target = event.target;
        if (target instanceof Element && target.closest(".work-inline-card-kind-btn, .work-inline-card-collab-remove")) markDirty();
      });
    }
    _wireMoreFields(el, record);
    // 미리 채운 프로젝트가 있으면 펼치기 전에도 회사 목록과 대조해 한 줄 요약이 등록될 값과 같게 한다.
    if (record.project) _syncProjects(el);
    _syncOwnerVisibility(el, record);

    // 팀원 목록·붙이기 대상 제목은 뒤늦게 채운다(카드는 먼저 보인다). 사용자가 이미 고른 값은 덮지 않는다.
    void _loadPeople().then(function (people) {
      var select = el.querySelector<HTMLSelectElement>(".work-inline-card-owner");
      if (!select || el.getAttribute("data-state") !== "pending_review") return;
      var chosen = select.value || ownerId;
      select.innerHTML = _ownerOptionsHtml(people, chosen, record);
    });
    if (record.target && record.target.type === "attach") {
      var targetItemId = record.target.workItemId;
      void _loadItemTitle(targetItemId).then(function (title) {
        var select = el.querySelector<HTMLSelectElement>(".work-inline-card-target");
        if (!select || !title) return;
        el.dataset.detectedTitle = title;
        select.innerHTML = _targetOptionsHtml(el, record, title, select.value);
      });
    }
  }

  // ===== 등록·무시 =====

  function _sessionGuard(el: HTMLElement): () => boolean {
    if (el.dataset.context !== "chat") return function () { return true; };
    var sessionId = window.currentSessionId;
    // await 이후 세션 재확인 — 요청 진행 중 방을 나가면 다른(현재) 방의 DOM을 건드리지 않는다.
    return function () { return window.currentSessionId === sessionId; };
  }

  function _resolve(el: HTMLElement, record: WorkCandidateRecord, options: WorkCandidateCardOptions, resolvedByLabel?: string): void {
    _applyCollapsedContent(el, record, resolvedByLabel);
    if (options.onResolved) options.onResolved(record);
  }

  // 409 수렴: 다시 읽어 처리됐으면 접고(이미 처리됨·만료), 아직 pending 이면(다른 요청이 처리 중이거나 후보가 바뀜)
  // 최신 내용으로 다시 그리고 다시 누를 수 있게 둔다.
  async function _reconcile(el: HTMLElement, outboxId: string, options: WorkCandidateCardOptions, stillCurrent: () => boolean, code: string | undefined): Promise<void> {
    try {
      var payload = await _fetchJson(_apiOutboxItem(outboxId));
      if (!stillCurrent()) return;
      var fresh = _parseWicRecord(payload.outboxItem);
      if (!fresh) return;
      if (fresh.state !== "pending_review") {
        _resolve(el, fresh, options);
        _toast(fresh.supersededBy ? "더 최신 후보로 바뀌었어요." : "이미 처리된 후보예요.");
        return;
      }
      // 다른 요청이 처리 중(lease)이면 끊긴 요청을 이어 갈 수 있게 같은 operationId 를 두고, 후보가 바뀌었으면 새로 시작한다.
      if (code === "outbox_revision_conflict") el.dataset.operationId = _newOperationId();
      // 프로젝트 모드 입력(모드·PO·참여자·트랙·고객·이름)은 다시 그려도 지킨다(검토 #1).
      var kept = _project.capture(el);
      _renderExpanded(el, fresh, options);
      _project.restore(el, fresh, kept);
      _setError(el, code === "outbox_revision_conflict"
        ? "후보 내용이 바뀌어 최신으로 다시 불러왔어요. 확인 후 다시 등록해 주세요."
        : "다른 곳에서 처리 중이에요. 잠시 뒤 다시 눌러 주세요.");
    } catch (error) {
      if (!stillCurrent()) return;
      _setBusy(el, false);
      _setError(el, "후보 상태를 확인하지 못했어요. 잠시 뒤 다시 시도해 주세요.");
    }
  }

  async function _registerCard(el: HTMLElement, record: WorkCandidateRecord, options: WorkCandidateCardOptions): Promise<void> {
    if (el.getAttribute("aria-busy") === "true" || el.getAttribute("data-state") !== "pending_review") return;
    var stillCurrent = _sessionGuard(el);
    var titleInput = el.querySelector<HTMLInputElement>(".work-inline-card-title-input");
    var ownerSelect = el.querySelector<HTMLSelectElement>(".work-inline-card-owner");
    var title = titleInput ? titleInput.value.trim() : record.title;
    if (!title) {
      _setError(el, el.dataset.kind === "project" ? "프로젝트 이름을 입력해 주세요." : "제목을 입력해 주세요.");
      if (titleInput) titleInput.focus();
      return;
    }
    if (el.dataset.kind === "project") {
      await _project.registerAsProject(el, record, options, title, stillCurrent);
      return;
    }
    var target = _selectedTarget(el, record);
    if (!target) {
      _setError(el, "하위 업무로 붙일 업무를 골라 주세요.");
      return;
    }
    var body: Record<string, unknown> = { title: title, target: target, operationId: el.dataset.operationId || _newOperationId() };
    var ownerId = ownerSelect ? ownerSelect.value : "";
    var createsItem = target.type === "new" || target.kind === "subitem";
    if (ownerId && createsItem) body.ownerActorId = ownerId;
    // 네 칸은 새 업무·하위 업무에서만, 처음 값에서 바뀐 칸만 보낸다(링크·파일 붙이기에는 키 자체를 싣지 않는다).
    if (createsItem) Object.assign(body, _changedFields(el, record));
    if (typeof record.revision === "number") body.expectedRevision = record.revision;
    _setError(el, "");
    _clearFieldErrors(el);
    _setBusy(el, true, "register");
    try {
      var payload = await _fetchJson(_apiOutboxItem(record.id) + "/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!stillCurrent()) return;
      var resolved = _parseWicRecord(payload.outboxItem);
      if (!resolved) return;
      if (!resolved.workItemId && typeof payload.workItemId === "string") resolved.workItemId = payload.workItemId;
      _resolve(el, resolved, options);
      _toast("업무로 등록했어요.");
    } catch (error) {
      if (!stillCurrent()) return;
      // 409 project_deleted 는 후보가 바뀐 충돌이 아니라 고른 프로젝트가 삭제된 것 — 다시 맞추지 않고 프로젝트 칸에 이유를 보인다.
      if (_statusOf(error) === 409 && _codeOf(error) !== "project_deleted") {
        await _reconcile(el, record.id, options, stillCurrent, _codeOf(error));
        return;
      }
      _setBusy(el, false);
      _setError(el, "등록하지 못했어요: " + (_codeOf(error) === "project_deleted" ? "그 사이 프로젝트가 삭제됐어요." : (error instanceof Error ? error.message : String(error))));
      _showFieldError(el, record, error);
    }
  }

  async function _dismissCard(el: HTMLElement, record: WorkCandidateRecord, options: WorkCandidateCardOptions): Promise<void> {
    if (el.getAttribute("aria-busy") === "true" || el.getAttribute("data-state") !== "pending_review") return;
    var stillCurrent = _sessionGuard(el);
    var body: Record<string, unknown> = {};
    if (typeof record.revision === "number") body.expectedRevision = record.revision;
    _setError(el, "");
    _setBusy(el, true, "dismiss");
    try {
      var payload = await _fetchJson(_apiOutboxItem(record.id) + "/dismiss", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!stillCurrent()) return;
      var resolved = _parseWicRecord(payload.outboxItem);
      if (!resolved) return;
      _resolve(el, resolved, options);
    } catch (error) {
      if (!stillCurrent()) return;
      if (_statusOf(error) === 409) {
        await _reconcile(el, record.id, options, stillCurrent, _codeOf(error));
        return;
      }
      _setBusy(el, false);
      _setError(el, "무시하지 못했어요: " + (error instanceof Error ? error.message : String(error)));
    }
  }

  // ===== 채팅 카드 =====

  function _findCard(host: Element, outboxId: string): HTMLElement | null {
    var existing = host.querySelectorAll<HTMLElement>(".work-inline-card");
    for (var i = 0; i < existing.length; i += 1) {
      if (existing[i].dataset.outboxId === outboxId) return existing[i];
    }
    return null;
  }

  // 보드 재정리(records-ssot 운영 배포·업무 이전, 2026-10-04) 기준 시각. 근거: 운영에 배포된 이전 도구 병합 커밋
  // 5507e618 의 시각(2026-10-04 18:16:57 +0900) — 배포는 이 커밋 뒤에만 가능했으므로 재정리의 가장 이른 방어 가능한 시점이다.
  // 이전 기록(docs/handoffs/records-ssot-implementation-handoff.md "운영 배포·이전 완료")은 같은 날 업무 이전·옛 후보 정리를
  // 마쳤고 재시작 뒤 새 채팅이 없었다고 적는다. 그 전에 만든 후보는 채팅 카드로 그리지 않는다.
  var _CHAT_CARD_CUTOFF = Date.parse("2026-10-04T09:16:57.000Z");

  // 채팅 카드로 그릴 후보인가: 감지 창 위치(anchorSeq)가 있고 기준 시각 이후에 만든 것만. msg_<seq> 좌표가 지금 뜻(세션 JSONL
  // 줄 번호)이 아닌 옛 레코드나 createdAt 이 없거나 깨진 레코드는 그리지 않는다. 시각은 오프셋이 섞여도 Date.parse 로 비교한다.
  function _isChatCardRecord(record: WorkCandidateRecord): boolean {
    if (record.anchorSeq === undefined) return false;
    var created = record.createdAt ? Date.parse(record.createdAt) : NaN;
    return !Number.isNaN(created) && created >= _CHAT_CARD_CUTOFF;
  }

  // 라이브·복원 두 채팅 경로가 모두 여기를 지난다 — 옛 후보 거름도 여기 한 곳이다.
  function _insertCard(record: WorkCandidateRecord, host: HTMLElement, follow = true): void {
    if (!_isChatCardRecord(record)) return;
    if (_findCard(host, record.id)) return;
    var el = buildCard(record, { context: "chat" });
    if (record.anchorSeq !== undefined) el.dataset.anchorSeq = String(record.anchorSeq);
    host.appendChild(el);
    _observePlacement(host);
    _syncPlacement(host);
    if (follow) host.scrollTop = host.scrollHeight;
  }

  // ===== 타임라인 위치 =====
  // 채팅 카드는 감지 창의 마지막 메시지(anchorSeq) 바로 뒤, 다음 메시지 앞에 놓는다 — 처리 여부와 무관하게 감지가 일어난
  // 자리에 남고 대화 끝에 쌓이지 않는다. 그 메시지가 아직 불러오지 않은(또는 보존 상한으로 잘린) 과거에 있으면 숨겨
  // 두었다가 스크롤 업으로 그 구간이 들어오면 제자리에 보인다. 메시지 DOM 은 불러오기·잘라내기·날짜 이동으로 계속 바뀌므로
  // 날짜 구분선(chat-timeline-surfaces.ts syncDateDividers)처럼 #chatMessages 의 자식 변경을 관찰해 다시 맞춘다. 제자리면
  // 건드리지 않아(멱등) 자기 이동이 만든 변경 관찰이 반복되지 않는다. anchorSeq 가 없는 옛 레코드는 채팅에 넣지 않는다(_isChatCardRecord).
  var _observedHosts = new WeakSet<HTMLElement>();

  function _observePlacement(host: HTMLElement): void {
    if (_observedHosts.has(host) || typeof MutationObserver === "undefined") return;
    _observedHosts.add(host);
    new MutationObserver(function () { _syncPlacement(host); }).observe(host, { childList: true });
  }

  function _nodeSeq(node: Element): number | null {
    if (!(node instanceof HTMLElement) || !node.classList.contains("message")) return null;
    var sessionId = node.dataset.sessionId;
    if (sessionId && sessionId !== window.currentSessionId) return null;
    var raw = node.dataset.msgSeq;
    var seq = raw === undefined ? NaN : Number(raw);
    return Number.isInteger(seq) && seq >= 0 ? seq : null;
  }

  function _syncPlacement(host: HTMLElement): void {
    var children = Array.from(host.children);
    var cards = children.filter(function (node): node is HTMLElement {
      return node instanceof HTMLElement && node.classList.contains("work-inline-card") && node.dataset.anchorSeq !== undefined;
    });
    if (!cards.length) return;
    var messages: Array<{ node: Element; seq: number }> = [];
    children.forEach(function (node) {
      var seq = _nodeSeq(node);
      if (seq !== null) messages.push({ node: node, seq: seq });
    });
    if (!messages.length) return;
    var oldest = Math.min.apply(null, messages.map(function (entry) { return entry.seq; }));
    var hasOlder = typeof messageListState !== "undefined" && messageListState.hasMore === true;
    // 스크롤 보정 기준은 옮기기 직전에 한 번만 잰다(제자리면 레이아웃을 읽지 않는다). 옮겨질 카드는 기준에서 뺀다.
    var view: { follow: boolean; top: number; anchor?: HTMLElement; anchorTop?: number } | null = null;
    var beforeMove = function () {
      if (view) return;
      var bounds = host.getBoundingClientRect();
      var anchorEl = children.find(function (node): node is HTMLElement {
        return node instanceof HTMLElement && !node.hidden && !node.classList.contains("work-inline-card") && node.getBoundingClientRect().bottom > bounds.top;
      });
      view = { follow: host.scrollHeight - host.scrollTop - host.clientHeight < 4, top: host.scrollTop, anchor: anchorEl, anchorTop: anchorEl ? anchorEl.offsetTop : undefined };
    };
    cards.forEach(function (card) {
      var anchor = Number(card.dataset.anchorSeq);
      // 근거 메시지가 아직 불러오지 않은 과거면 맨 끝에 숨겨 둔다. 맨 위에 두면 숨은 카드가 첫 자식이 되어 과거 불러오기
      // (prependHistory)의 스크롤 보정 기준(firstElementChild.offsetTop)이 0 으로 굳는다.
      if (anchor < oldest && hasOlder) {
        if (!card.hidden) {
          beforeMove();
          host.appendChild(card);
          card.hidden = true;
        }
        return;
      }
      // 제자리 = 카드 바로 다음 메시지가 "anchor 보다 뒤인 첫 메시지"(없으면 카드 뒤에 메시지가 없음). 다음 메시지만으로
      // 판정해야 메시지 순서가 어긋난 DOM 에서도 한 번 옮긴 뒤 다시 옮기지 않는다(관찰 루프 방지).
      var target = messages.filter(function (entry) { return entry.seq > anchor; })[0];
      var nextEl: Element | null = target ? target.node : null;
      var following = card.nextElementSibling;
      while (following && _nodeSeq(following) === null) following = following.nextElementSibling;
      var wasHidden = card.hidden;
      if (following === nextEl && !wasHidden) return;
      beforeMove();
      card.hidden = false;
      if (following === nextEl) return;
      if (nextEl) {
        var divider = nextEl.previousElementSibling;
        host.insertBefore(card, divider && divider.classList.contains("chat-date-divider") ? divider : nextEl);
      } else {
        host.appendChild(card);
      }
    });
    var settled = view as { follow: boolean; top: number; anchor?: HTMLElement; anchorTop?: number } | null;
    if (!settled) return;
    if (settled.follow) host.scrollTop = host.scrollHeight;
    else if (settled.anchor && settled.anchor.parentElement === host && settled.anchorTop !== undefined) host.scrollTop = settled.top + settled.anchor.offsetTop - settled.anchorTop;
  }

  // 채팅 카드의 [등록]·[무시]를 id 로 누른다(키보드 단축·테스트에서 직접 부를 수 있게 host 에서 카드를 다시 찾는다).
  function register(outboxId: string): Promise<void> {
    var host = _chatMessagesEl();
    var btn = host && _findCard(host, outboxId)?.querySelector<HTMLButtonElement>(".work-inline-card-register-btn");
    if (btn) btn.click();
    return Promise.resolve();
  }

  function dismiss(outboxId: string): Promise<void> {
    var host = _chatMessagesEl();
    var btn = host && _findCard(host, outboxId)?.querySelector<HTMLButtonElement>(".work-inline-card-dismiss-btn");
    if (btn) btn.click();
    return Promise.resolve();
  }

  // ===== 라이브 경로 (ws-handlers.ts work-detected 위임) =====

  async function onWorkDetected(data: unknown): Promise<void> {
    if (!_isWicRecord(data) || typeof data.sessionId !== "string" || data.sessionId !== window.currentSessionId) return;
    var sessionId = data.sessionId;
    var host = _chatMessagesEl();
    if (!host) return;
    var outboxIds = Array.isArray(data.outboxIds)
      ? data.outboxIds.filter((id): id is string => typeof id === "string")
      : [];
    for (var i = 0; i < outboxIds.length; i += 1) {
      var outboxId = outboxIds[i];
      if (_findCard(host, outboxId)) continue;
      try {
        var payload = await _fetchJson(_apiOutboxItem(outboxId));
        // await 이후 세션 재확인 — 조회 중 세션이 바뀌면 이전 세션 카드를 현재 방에 넣지 않는다(루프마다 확인).
        if (window.currentSessionId !== sessionId) return;
        var record = _parseWicRecord(payload.outboxItem);
        if (record) _insertCard(record, host);
      } catch (error) {
        console.error("[work-inline-cards] 상세 조회 실패:", error instanceof Error ? error.message : String(error));
      }
    }
  }

  // ===== 복원 경로 (D02, CAP06) =====

  async function restore(sessionId?: string | null): Promise<void> {
    var host = _chatMessagesEl();
    if (!host || !sessionId) return;
    var chatId = "chat:" + sessionId;
    try {
      var data = await _fetchJson(_apiOutbox());
      // chatId 필터는 "레코드가 어느 방 소속인가"만 거르므로 "지금 그 방을 보고 있는가"는 따로 확인한다.
      if (window.currentSessionId !== sessionId) return;
      var items = Array.isArray(data.items) ? data.items : [];
      var records = items
        .map(_parseWicRecord)
        .filter((record): record is WorkCandidateRecord => record !== null)
        .filter(function (record) { return record.chatId === chatId; })
        .sort(function (a, b) { return String(a.createdAt || "").localeCompare(String(b.createdAt || "")); });
      // 응답을 기다리는 동안 과거를 읽기 시작한 사용자를 맨 아래로 끌어내리지 않는다.
      var follow = host.scrollHeight - host.scrollTop - host.clientHeight < 4;
      records.forEach(function (record) { _insertCard(record, host as HTMLElement, follow); });
    } catch (error) {
      console.error("[work-inline-cards] restore 실패:", error instanceof Error ? error.message : String(error));
    }
  }

  // ===== 처리 동기화 (CAP11, ws-handlers.ts work-outbox-resolved 위임) =====
  // 다른 멤버(또는 다른 화면의 나)가 처리한 결과를 그 자리에서 접힌 한 줄로 반영한다. 등록 이벤트는 workItemId 를 싣는다.
  function onWorkOutboxResolved(data: unknown): void {
    if (!_isWicRecord(data) || typeof data.sessionId !== "string" || data.sessionId !== window.currentSessionId) return;
    var outboxId = typeof data.outboxId === "string" ? data.outboxId : "";
    var resolution = typeof data.resolution === "string" ? data.resolution : "";
    if (!outboxId || (resolution !== "converted" && resolution !== "dismissed")) return;
    var host = _chatMessagesEl();
    var cardEl = host && _findCard(host, outboxId);
    if (!cardEl || cardEl.getAttribute("data-state") !== "pending_review") return;
    // 대체됐는데 카드에 저장하지 않은 입력이 있으면 접지 않는다: 입력을 둔 채 상단 배너만 띄운다(검토 #6). [등록]·[무시]는
    // 서버 409(+ supersededBy) 경로로 접힌다.
    if (typeof data.supersededBy === "string" && cardEl.dataset.dirty === "true") {
      _showNewerBanner(cardEl, data.supersededBy);
      return;
    }
    var record: WorkCandidateRecord = {
      id: outboxId,
      state: resolution,
      title: _cardTitles[outboxId] || "업무 후보",
      workItemId: typeof data.workItemId === "string" ? data.workItemId : undefined,
      proposalId: typeof data.proposalId === "string" ? data.proposalId : undefined,
      projectId: typeof data.projectId === "string" ? data.projectId : undefined,
      supersededBy: typeof data.supersededBy === "string" ? data.supersededBy : undefined,
    };
    _applyCollapsedContent(cardEl, record, _actorLabel(data.resolvedBy));
  }

  function _showNewerBanner(cardEl: HTMLElement, newerId: string): void {
    if (cardEl.querySelector(".work-inline-card-newer-banner")) return;
    var banner = document.createElement("div");
    banner.className = "work-inline-card-newer-banner";
    banner.setAttribute("role", "status");
    var text = document.createElement("span");
    text.textContent = "더 최신 후보로 바뀌었어요";
    var open = document.createElement("button");
    open.type = "button";
    open.className = "work-inline-card-board-link work-inline-card-newer-link";
    open.textContent = "새 카드 열기";
    open.addEventListener("click", function () { _openNewerCard(newerId); });
    banner.appendChild(text);
    banner.appendChild(open);
    cardEl.insertBefore(banner, cardEl.firstChild);
  }

  // 프로젝트 모드·생성 카드(work-inline-cards-project.ts). 이 IIFE 의 도우미를 넘긴다.
  var _project = _createWorkCardProject({
    escape: _escape, viewerId: _viewerId, loadPeople: _loadPeople, loadCompany: _loadCompany,
    resetCompany: function () { _companyPromise = null; },
    fieldStates: _fieldStates, fieldState: _fieldState, moreFieldsHtml: _moreFieldsHtml, wireMoreFields: _wireMoreFields,
    setError: _setError, setBusy: _setBusy, clearFieldErrors: _clearFieldErrors, sessionGuard: _sessionGuard, fetchJson: _fetchJson,
    toast: _toast, statusOf: _statusOf, codeOf: _codeOf, payloadOf: _payloadOf, newOperationId: _newOperationId,
    chatMessagesEl: _chatMessagesEl, dateToIso: _dateToIso, syncOwnerVisibility: _syncOwnerVisibility, projectBoardLink: _projectBoardLink,
    apiOutboxItem: _apiOutboxItem, reconcile: _reconcile, resolve: _resolve,
  });

  return {
    buildCard: buildCard,
    buildCreateCard: _project.buildCreateCard,
    handleCommandResult: _project.handleCommandResult,
    parseRecord: _parseWicRecord,
    onWorkDetected: onWorkDetected,
    restore: restore,
    onWorkOutboxResolved: onWorkOutboxResolved,
    // 업무 화면 첫 화면(views/work/my-work.ts)이 입력 중 카드의 대체를 채팅과 같은 배너로 알릴 때 쓴다.
    showNewerBanner: _showNewerBanner,
    register: register,
    dismiss: dismiss,
  };
})();
